import urls from "global/constants/UrlConstants";
import { WorkspaceApiResponse, WorkspaceMembership } from "types/workspace";
import { getCallParams, makeCall } from "utils/service";
import strings from "global/constants/StringConstants";
import { hasAccessTo } from "layouts/Config";

/** A pickable account user for the add-member autocomplete. */
export interface AccountMember {
  name: string;
  email: string;
}

/**
 * Type-ahead search over the account's own users (same endpoint the Teams screen uses).
 * Returns a small page so the dropdown shows ~5 suggestions that narrow as the user types.
 * Workspace members can only be existing account users, so this is the source of truth.
 */
export const searchAccountMembers = async (
  searchText: string,
  limit: number = 5,
): Promise<AccountMember[]> => {
  try {
    // The Teams endpoint is for account admins. A delegated workspace admin uses the signed-in
    // user search instead, which also covers only this account's users and takes the raw text.
    const accountAdmin = hasAccessTo(strings.resources.app, strings.adminPermission);
    const url = accountAdmin
      ? `${urls.GET_ALL_USER_WITH_SEARCH}/limit/${limit}/page/0`
      : `${urls.SECURE_USER_SEARCH}/limit/${limit}/page/0`;
    const callParams = accountAdmin
      ? await getCallParams("POST", { searchText })
      : { ...(await getCallParams("POST")), body: searchText };
    const response: any = await makeCall(url, callParams);
    const list = Array.isArray(response) ? response : (response?.data ?? []);
    return list
      .filter((u: any) => u && u.email)
      .map((u: any) => ({
        name: u.name || `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim() || u.email,
        email: u.email,
      }));
  } catch (error: any) {
    throw error;
  }
};

export const fetchWorkspaceMembers = async (
  workspaceId: string,
): Promise<WorkspaceMembership[]> => {
  try {
    const callParams = await getCallParams("GET");
    const response: WorkspaceMembership[] = await makeCall(
      `${urls.WORKSPACE_BASE}/${workspaceId}/member/fetch`,
      callParams,
    );
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const addWorkspaceMember = async (
  workspaceId: string,
  userEmail: string,
  roleId: number,
): Promise<WorkspaceApiResponse> => {
  try {
    const callParams = await getCallParams("POST", { userEmail, roleId });
    const response: WorkspaceApiResponse = await makeCall(
      `${urls.WORKSPACE_BASE}/${workspaceId}/member/add`,
      callParams,
    );
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const removeWorkspaceMember = async (
  membershipId: string,
): Promise<WorkspaceApiResponse> => {
  try {
    const callParams = await getCallParams("DELETE");
    const response: WorkspaceApiResponse = await makeCall(
      `${urls.WORKSPACE_MEMBER_DELETE}/${membershipId}`,
      callParams,
    );
    return response;
  } catch (error: any) {
    throw error;
  }
};
